import { useEffect, useRef, useState } from 'react';

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
    CircleX,
    Clock,
    Monitor,
    ShieldCheck,
    TrendingUp,
    Zap,
} from 'lucide-react';

import { LanguageToggle } from '@/components/language-toggle';
import { BASE_URL } from '@/queries/api/apiUtils';

// ─── Shared layout helpers ────────────────────────────────────────────────────

function SectionContainer({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`w-full ${className}`}>
            <div className="max-w-[1120px] mx-auto px-6 md:px-8">
                {children}
            </div>
        </div>
    );
}

function SectionLabel({ children, light = false }: { children: React.ReactNode; light?: boolean }) {
    return (
        <p className={`text-[13px] font-bold uppercase tracking-[1.56px] ${light ? 'text-lp-gold' : 'text-lp-gold-dark'}`}>
            {children}
        </p>
    );
}

function SectionHeading({ children, light = false, className = '' }: { children: React.ReactNode; light?: boolean; className?: string }) {
    return (
        <h2 className={`font-garamond font-semibold text-[36px] md:text-[46px] leading-[1.08] tracking-[-0.46px] ${light ? 'text-white' : 'text-lp-dark'} ${className}`}>
            {children}
        </h2>
    );
}

function GoldButton({ children, className = '', ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string }) {
    return (
        <button
            className={`bg-gradient-lp-gold text-lp-dark font-bold text-[15px] px-6 py-3 rounded-[10px] shadow-[0_4px_7px_rgba(168,120,29,0.32)] hover:opacity-90 transition-opacity cursor-pointer ${className}`}
            {...props}
        >
            {children}
        </button>
    );
}

function GoldLinkButton({ to, children, className = '' }: { to: string; children: React.ReactNode; className?: string }) {
    return (
        <Link
            to={to}
            className={`no-underline inline-flex items-center justify-center bg-gradient-lp-gold text-lp-dark font-bold text-[15px] px-6 py-3 rounded-[10px] shadow-[0_4px_7px_rgba(168,120,29,0.32)] hover:opacity-90 transition-opacity ${className}`}
        >
            {children}
        </Link>
    );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

function Nav() {
    const { t } = useTranslation('landing');
    return (
        <header className="sticky top-0 z-50 backdrop-blur-[4px] bg-white/92 border-b border-lp-border">
            <div className="max-w-[1120px] mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
                <img src="/newlogo.svg" alt={t('footer.logoAlt')} className="h-8 w-auto" />
                <nav className="flex items-center gap-4 md:gap-5">
                    <LanguageToggle variant="compact" />
                    <Link
                        to="/auth/login"
                        className="no-underline text-lp-muted text-sm font-semibold hover:text-lp-dark transition-colors"
                    >
                        {t('nav.logIn')}
                    </Link>
                    <GoldLinkButton to="/auth/signup">
                        {t('nav.signUp')}
                    </GoldLinkButton>
                </nav>
            </div>
        </header>
    );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function HeroSection() {
    const { t } = useTranslation('landing');
    return (
        <section className="bg-gradient-lp-hero w-full py-[88px]">
            <SectionContainer>
                <div className="flex flex-col gap-6 max-w-[600px]">
                    <p className="font-garamond text-lp-gold text-[28px] md:text-[36px] leading-tight">
                        {t('hero.eyebrow')}
                    </p>

                    <h1 className="font-garamond font-semibold text-white text-[52px] md:text-[76px] leading-[1.02] tracking-[-0.76px]">
                        {t('hero.title')}
                    </h1>

                    <p className="text-white/88 text-[21px] leading-[1.55]">
                        {t('hero.subtitle')}
                    </p>

                    <div className="inline-flex items-center gap-2 bg-[rgba(200,150,46,0.12)] border border-[rgba(227,184,90,0.3)] rounded-full px-4 py-2 self-start">
                        <span className="w-2 h-2 rounded-[4px] bg-lp-gold shadow-[0_0_0_4px_rgba(227,184,90,0.25)]" />
                        <span className="text-lp-gold text-sm font-semibold">
                            {t('hero.betaBadge')}
                        </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 pt-1">
                        <GoldLinkButton to="/auth/signup">
                            {t('hero.primaryCta')}
                        </GoldLinkButton>
                        <Link
                            to="/auth/signup"
                            className="text-white! text-sm font-semibold no-underline hover:text-white transition-colors"
                        >
                            {t('hero.waitlistLink')}
                        </Link>
                    </div>
                </div>
            </SectionContainer>
        </section>
    );
}

// ─── How It Works ─────────────────────────────────────────────────────────────

function HowItWorksSection() {
    const { t } = useTranslation('landing');
    const steps = [
        { n: '1', title: t('howItWorks.steps.one.title'), body: t('howItWorks.steps.one.body') },
        { n: '2', title: t('howItWorks.steps.two.title'), body: t('howItWorks.steps.two.body') },
        { n: '3', title: t('howItWorks.steps.three.title'), body: t('howItWorks.steps.three.body') },
    ];

    return (
        <section className="bg-lp-cream w-full py-[84px]">
            <SectionContainer>
                <div className="flex flex-col items-center gap-3 text-center">
                    <SectionLabel>{t('howItWorks.eyebrow')}</SectionLabel>
                    <SectionHeading>{t('howItWorks.title')}</SectionHeading>
                    <p className="text-lp-muted text-[18px] mt-1">{t('howItWorks.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                    {steps.map((step) => (
                        <div
                            key={step.n}
                            className="bg-white border border-lp-border rounded-[16px] px-7 py-8 flex flex-col gap-3"
                        >
                            <div
                                className="w-11 h-11 rounded-[12px] bg-gradient-lp-gold flex items-center justify-center shrink-0"
                            >
                                <span className="font-garamond font-semibold text-lp-dark text-[22px] leading-none">
                                    {step.n}
                                </span>
                            </div>
                            <h3 className="font-bold text-lp-dark text-[20px] leading-[1.55] mt-1">
                                {step.title}
                            </h3>
                            <p className="text-lp-muted text-[15px] leading-[1.55]">
                                {step.body}
                            </p>
                        </div>
                    ))}
                </div>
            </SectionContainer>
        </section>
    );
}

// ─── Why ──────────────────────────────────────────────────────────────────────

function WhySection() {
    const { t } = useTranslation('landing');
    const problems = [
        {
            icon: <Clock className="w-[22px] h-[22px] text-[#6e1715]" />,
            title: t('why.problems.flexibility.title'),
            body: t('why.problems.flexibility.body'),
        },
        {
            icon: <TrendingUp className="w-[22px] h-[22px] text-[#6e1715]" />,
            title: t('why.problems.demand.title'),
            body: t('why.problems.demand.body'),
        },
        {
            icon: <CircleX className="w-[22px] h-[22px] text-[#6e1715]" />,
            title: t('why.problems.solutions.title'),
            body: t('why.problems.solutions.body'),
        },
    ];

    return (
        <section className="bg-white w-full py-[84px]">
            <SectionContainer>
                <div className="flex flex-col items-center gap-3 text-center">
                    <SectionLabel>{t('why.eyebrow')}</SectionLabel>
                    <SectionHeading>{t('why.title')}</SectionHeading>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                    {problems.map((p) => (
                        <div
                            key={p.title}
                            className="bg-white border border-lp-border rounded-[16px] px-7 py-8 flex flex-col gap-4"
                        >
                            <div className="w-[46px] h-[46px] rounded-[12px] bg-lp-icon flex items-center justify-center shrink-0">
                                {p.icon}
                            </div>
                            <h3 className="font-bold text-lp-dark text-[19px] leading-[1.55]">
                                {p.title}
                            </h3>
                            <p className="text-lp-muted text-[15px] leading-[1.55]">
                                {p.body}
                            </p>
                        </div>
                    ))}
                </div>

                <p className="text-center font-garamond text-lp-dark text-[22px] md:text-[24px] leading-[1.4] mt-10 max-w-[714px] mx-auto">
                    {t('why.summaryPrefix')}
                    <strong className="font-bold text-[#6e1715]">
                        {t('why.summaryHighlight')}
                    </strong>
                    {t('why.summarySuffix')}
                </p>
            </SectionContainer>
        </section>
    );
}

// ─── What You Get ─────────────────────────────────────────────────────────────

function CheckItem({ text }: { text: string }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-[14px] px-6 py-5 flex items-start gap-4">
            <div className="w-[26px] h-[26px] rounded-full bg-lp-gold/18 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-lp-gold text-[16px] leading-none">✓</span>
            </div>
            <p className="text-white/92 text-[16px] leading-[1.55]">{text}</p>
        </div>
    );
}

function WhatYouGetSection() {
    const { t } = useTranslation('landing');
    const [activeTab, setActiveTab] = useState<'workers' | 'restaurants'>('workers');

    const workerBenefits = [
        t('whatYouGet.workerBenefits.schedule'),
        t('whatYouGet.workerBenefits.discovery'),
        t('whatYouGet.workerBenefits.history'),
        t('whatYouGet.workerBenefits.free'),
    ];
    const restaurantBenefits = [
        t('whatYouGet.restaurantBenefits.vetted'),
        t('whatYouGet.restaurantBenefits.post'),
        t('whatYouGet.restaurantBenefits.workforce'),
        t('whatYouGet.restaurantBenefits.freeBeta'),
    ];

    const benefits = activeTab === 'workers' ? workerBenefits : restaurantBenefits;
    const ctaText = activeTab === 'workers' ? t('whatYouGet.ctaWorkers') : t('whatYouGet.ctaRestaurants');

    return (
        <section className="bg-lp-dark w-full py-[84px]">
            <SectionContainer>
                <div className="flex flex-col items-center gap-4 text-center">
                    <SectionHeading light>{t('whatYouGet.title')}</SectionHeading>

                    <div className="flex gap-1 bg-white/8 rounded-[14px] p-[5px] mt-1">
                        <button
                            onClick={() => setActiveTab('workers')}
                            className={`px-8 py-3 rounded-[10px] font-bold text-[16px] transition-all cursor-pointer ${
                                activeTab === 'workers'
                                    ? 'bg-gradient-lp-gold text-lp-dark'
                                    : 'text-white/70 hover:text-white'
                            }`}
                        >
                            {t('whatYouGet.tabs.workers')}
                        </button>
                        <button
                            onClick={() => setActiveTab('restaurants')}
                            className={`px-8 py-3 rounded-[10px] font-bold text-[16px] transition-all cursor-pointer ${
                                activeTab === 'restaurants'
                                    ? 'bg-gradient-lp-gold text-lp-dark'
                                    : 'text-white/70 hover:text-white'
                            }`}
                        >
                            {t('whatYouGet.tabs.restaurants')}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-[18px] mt-4 w-full max-w-[880px]">
                        {benefits.map((b) => (
                            <CheckItem key={b} text={b} />
                        ))}
                    </div>

                    <GoldLinkButton to="/auth/signup" className="mt-4">
                        {ctaText}
                    </GoldLinkButton>
                </div>
            </SectionContainer>
        </section>
    );
}

// ─── About ────────────────────────────────────────────────────────────────────

function AboutSection() {
    const { t } = useTranslation('landing');
    const values = [
        {
            icon: <ShieldCheck className="w-5 h-5 text-[#6e1715]" />,
            title: t('about.values.transparent.title'),
            body: t('about.values.transparent.body'),
        },
        {
            icon: <Monitor className="w-5 h-5 text-[#6e1715]" />,
            title: t('about.values.modern.title'),
            body: t('about.values.modern.body'),
        },
        {
            icon: <Zap className="w-5 h-5 text-[#6e1715]" />,
            title: t('about.values.flexible.title'),
            body: t('about.values.flexible.body'),
        },
    ];

    return (
        <section className="bg-lp-cream w-full py-[84px]">
            <SectionContainer>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
                    <div className="flex flex-col gap-4">
                        <SectionLabel>{t('about.eyebrow')}</SectionLabel>
                        <SectionHeading>{t('about.title')}</SectionHeading>
                        <p className="text-lp-muted text-[18px] leading-[1.55] mt-1">
                            {t('about.subtitle')}
                        </p>
                    </div>

                    <div className="flex flex-col gap-10">
                        {values.map((v) => (
                            <div key={v.title} className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-[10px] bg-white border border-lp-border flex items-center justify-center shrink-0">
                                    {v.icon}
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <p className="font-bold text-lp-dark text-[22px] leading-tight">{v.title}</p>
                                    <p className="text-lp-muted text-[15px] leading-[1.55]">{v.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </SectionContainer>
        </section>
    );
}

// ─── Sign Up / CTA ────────────────────────────────────────────────────────────

function SignUpSection() {
    const { t } = useTranslation('landing');
    const [email, setEmail] = useState('');
    const [city, setCity] = useState('');
    const [intent, setIntent] = useState<'WORKER' | 'RESTAURANT'>('WORKER');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    async function handleWaitlist(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${BASE_URL}/public/waitlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), city: city.trim(), intent }),
            });
            if (!res.ok) throw new Error('Request failed');
            setSubmitted(true);
        } catch {
            setError(t('cta.errorGeneric'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <section className="bg-gradient-lp-cta w-full py-[84px]">
            <SectionContainer>
                <div className="flex flex-col items-center gap-4 text-center">
                    <SectionLabel light>{t('cta.eyebrow')}</SectionLabel>
                    <SectionHeading light>{t('cta.title')}</SectionHeading>
                    <p className="text-white/85 text-[18px]">{t('cta.subtitle')}</p>

                    <div className="flex flex-wrap items-center justify-center gap-4 mt-2">
                        <GoldLinkButton to="/auth/signup" className="px-10 whitespace-nowrap">{t('cta.signUp')}</GoldLinkButton>
                        <Link
                            to="/auth/login"
                            className="no-underline inline-flex items-center justify-center bg-white/12 border border-white/35 text-white! font-bold text-[15px] px-6 py-3 rounded-[10px] hover:bg-white/20 transition-colors"
                        >
                            {t('cta.logIn')}
                        </Link>
                    </div>

                    <div className="bg-white/7 border border-white/18 rounded-[18px] px-7 py-7 w-full max-w-[520px] flex flex-col gap-4 mt-4">
                        <p className="font-garamond italic text-lp-gold text-[18px] text-center leading-[1.55]">
                            {t('cta.waitlistHeadline')}
                        </p>
                        {submitted ? (
                            <p className="text-white/85 text-[15px] text-center py-2">
                                {t('cta.thanks')}
                            </p>
                        ) : (
                            <form onSubmit={handleWaitlist} className="flex flex-col gap-3">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('cta.emailPlaceholder')}
                                    required
                                    className="bg-white/95 border border-white/25 rounded-[10px] px-4 py-3 text-[15px] text-[#222] placeholder:text-[#757575] outline-none focus:ring-2 focus:ring-lp-gold/50"
                                />
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder={t('cta.cityPlaceholder')}
                                    required
                                    className="bg-white/95 border border-white/25 rounded-[10px] px-4 py-3 text-[15px] text-[#222] placeholder:text-[#757575] outline-none focus:ring-2 focus:ring-lp-gold/50"
                                />
                                <div className="flex gap-1 bg-white/8 rounded-[12px] p-1">
                                    <button
                                        type="button"
                                        onClick={() => setIntent('WORKER')}
                                        className={`flex-1 py-2.5 rounded-[9px] font-bold text-[14px] transition-all cursor-pointer ${
                                            intent === 'WORKER'
                                                ? 'bg-gradient-lp-gold text-lp-dark'
                                                : 'text-white/70 hover:text-white'
                                        }`}
                                    >
                                        {t('cta.intentWorker')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIntent('RESTAURANT')}
                                        className={`flex-1 py-2.5 rounded-[9px] font-bold text-[14px] transition-all cursor-pointer ${
                                            intent === 'RESTAURANT'
                                                ? 'bg-gradient-lp-gold text-lp-dark'
                                                : 'text-white/70 hover:text-white'
                                        }`}
                                    >
                                        {t('cta.intentRestaurant')}
                                    </button>
                                </div>
                                {error && (
                                    <p className="text-red-300 text-[13px] text-center">{error}</p>
                                )}
                                <GoldButton type="submit" disabled={loading} className="w-full">
                                    {loading ? t('cta.submitting') : t('cta.submit')}
                                </GoldButton>
                            </form>
                        )}
                    </div>
                </div>
            </SectionContainer>
        </section>
    );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer({ stickyBarVisible }: { stickyBarVisible: boolean }) {
    const { t } = useTranslation('landing');
    const { t: tCommon } = useTranslation('common');
    return (
        <footer className={`bg-lp-dark w-full pt-12 transition-[padding] duration-300 ${stickyBarVisible ? 'pb-[84px]' : 'pb-12'}`}>
            <SectionContainer>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <img src="/newlogo.svg" alt={t('footer.logoAlt')} className="h-9 w-auto brightness-0 invert opacity-90" />
                    </div>
                    <div className="flex items-center gap-6 text-white/80 text-[14px]">
                        <a
                            href="mailto:info@publicmarket.com"
                            className="no-underline text-white/80! hover:text-white! transition-colors"
                        >
                            {t('footer.emailLabel')}
                        </a>
                        <a
                            href="https://www.linkedin.com/company/publicmkt"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="no-underline text-white/80! hover:text-white! transition-colors"
                        >
                            {t('footer.linkedinLabel')}
                        </a>
                        <span className="text-white/50 text-[13px]">{tCommon('footer.copyright')}</span>
                    </div>
                </div>
            </SectionContainer>
        </footer>
    );
}

// ─── Sticky Bar ───────────────────────────────────────────────────────────────

function StickyBar({ visible }: { visible: boolean }) {
    const { t } = useTranslation('landing');
    return (
        <div
            className={`fixed bottom-0 left-0 right-0 z-40 backdrop-blur-[4px] bg-[rgba(43,20,24,0.97)] border-t border-lp-gold/30 transition-transform duration-300 ${
                visible ? 'translate-y-0' : 'translate-y-full'
            }`}
        >
            <div className="max-w-[1120px] mx-auto px-6 md:px-8 py-3 flex items-center justify-between">
                <div>
                    <p className="text-white font-semibold text-[15px] leading-tight">
                        {t('stickyBar.title')}
                    </p>
                    <p className="text-white/70 text-[13px]">
                        {t('stickyBar.subtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to="/auth/signup"
                        className="no-underline text-white! font-semibold text-[14px] hover:text-white transition-colors"
                    >
                        {t('stickyBar.waitlist')}
                    </Link>
                    <GoldLinkButton to="/auth/signup" className="py-2 px-5 text-[15px]">
                        {t('stickyBar.signUp')}
                    </GoldLinkButton>
                </div>
            </div>
        </div>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function HomePage() {
    const heroRef = useRef<HTMLDivElement>(null);
    const [showStickyBar, setShowStickyBar] = useState(false);

    useEffect(() => {
        const hero = heroRef.current;
        if (!hero) return;
        const observer = new IntersectionObserver(
            ([entry]) => setShowStickyBar(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(hero);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="min-h-screen flex flex-col">
            <Nav />
            <main className="flex-1">
                <div ref={heroRef}>
                    <HeroSection />
                </div>
                <HowItWorksSection />
                <WhySection />
                <WhatYouGetSection />
                <AboutSection />
                <SignUpSection />
            </main>
            <Footer stickyBarVisible={showStickyBar} />
            <StickyBar visible={showStickyBar} />
        </div>
    );
}

export const Route = createFileRoute('/')({
    beforeLoad: ({ context }) => {
        if (context.auth?.isAuthenticated && context.auth.user) {
            throw redirect({
                to: '/app/$role/dashboard',
                params: { role: context.auth.user.role },
            });
        }
    },
    component: HomePage,
});
